<script setup lang="ts">
/*
 * The name line of an open price-list project: which project this is, and whether it is safe to close
 * — the two things Canva keeps together, for the same reason: a name alone does not say whether the
 * work is out of danger.
 *
 * Renaming in place — the same pattern the price list uses for its chapter labels (input, save on
 * enter or on leaving the field). The generated name is a starting point, not a decision, so changing
 * it has to be as cheap as possible. This component only says what was typed; writing it is the
 * project's business (usePriceListProject#rename).
 *
 * Interpolated, not v-html: the name is user-typed, and that is not a place for markup.
 */
const p = defineProps<{
  name: string
  // "Saving…" / "Not saved — retrying", empty when there is nothing to say (see usePriceListProject).
  saveLabel: string
  // Only the name, no pencil: a locked template project shows which template it is (15.10.2026,
  // customer request) — renaming it would suggest a project of one's own that does not exist yet.
  readonly?: boolean
}>()

const emit = defineEmits<{
  rename: [string]
}>()

const editing: Ref<boolean> = ref(false)
const draft: Ref<string> = ref('')

function start(): void {
  draft.value = p.name
  editing.value = true
}

function save(): void {
  editing.value = false
  emit('rename', draft.value.trim())
}
</script>

<template>
  <div class="mt-4 text-sm flex items-center gap-2">
    <UInput
      v-if="editing"
      v-model="draft"
      autofocus
      class="w-96 max-w-full"
      @blur="save()"
      @keyup.enter="save()"
    />
    <!-- The pencil right after the name it edits, the save state behind it (14.10.2026, Christoph):
         the state comes and goes, and the pencil must not jump with it. -->
    <template v-else>
      <span>{{ name }}</span>
      <UButton v-if="!readonly" icon="i-wilo-pen-solid-full" variant="ghost" color="tertiary" size="xs" @click="start()" />
      <span v-if="saveLabel">{{ saveLabel }}</span>
    </template>
  </div>
</template>
